// ============================================
// SUPABASE CLIENT CONFIGURATION
// ============================================
// Replace these with your actual Supabase credentials

import { createClient } from '@supabase/supabase-js';

// MavStream Supabase Configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://kflaiwzzpevmidmjqbiw.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtmbGFpd3p6cGV2bWlkbWpxYml3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzIwNjk3NDQsImV4cCI6MjA4NzY0NTc0NH0.qpp6zC4tNCZBh-4ZZWaKTPlVBeBoRENq3-Qq-JtWzyU';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============================================
// DATABASE HELPER FUNCTIONS
// ============================================

// ----- USERS -----

export async function createUser(userData) {
  // Try with password first, fall back to without if column doesn't exist
  let insertData = {
    email: userData.email,
    company_legal_name: userData.companyLegalName,
    brand_name_dba: userData.brandNameDBA,
    first_name: userData.firstName,
    last_name: userData.lastName,
    phone: userData.phone,
    website: userData.website,
    account_type: userData.accountType
  };
  
  // Only include password if provided (column may not exist yet)
  if (userData.password) {
    insertData.password = userData.password;
  }
  
  const { data, error } = await supabase
    .from('users')
    .insert([insertData])
    .select()
    .single();
  
  if (error) {
    // If error is about password column not existing, try without it
    if (error.message && error.message.includes('password')) {
      console.warn('Password column may not exist, trying without it...');
      delete insertData.password;
      const { data: retryData, error: retryError } = await supabase
        .from('users')
        .insert([insertData])
        .select()
        .single();
      
      if (retryError) {
        console.error('Error creating user (retry):', retryError);
        throw retryError;
      }
      return retryData;
    }
    
    console.error('Error creating user:', error);
    throw error;
  }
  return data;
}

export async function getUserByEmail(email) {
  try {
    // Use maybeSingle() instead of single() to avoid errors when no row found
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email.toLowerCase().trim())
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching user by email:', error);
      return null;
    }
    return data;
  } catch (err) {
    console.error('Exception in getUserByEmail:', err);
    return null;
  }
}

export async function getAllUsers() {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching users:', error);
    return [];
  }
  return data || [];
}

// ----- PUBLISHER PROFILES -----

export async function createPublisherProfile(userId, profileData, userData = {}) {
  const { data, error } = await supabase
    .from('publisher_profiles')
    .insert([{
      user_id: userId,
      company_name: userData.companyLegalName || profileData.companyName || '',
      brand_name: userData.brandNameDBA || profileData.brandName || '',
      email: userData.email || profileData.email || '',
      company_description: profileData.companyDescription,
      city: profileData.city,
      state: profileData.state,
      primary_category: profileData.primaryCategory,
      list_size: profileData.listSize,
      engaged_subscribers: profileData.engagedSubscribers,
      website_visitors: profileData.websiteVisitors,
      social_followers: profileData.socialFollowers,
      dr_score: profileData.drScore,
      placement_types: profileData.placementTypes || [],
      regions: profileData.regions || [],
      gender_mix: profileData.genderMix,
      age_ranges: profileData.ageRanges || [],
      household_incomes: profileData.householdIncomes || [],
      monthly_revenue: profileData.monthlyRevenue,
      average_order_value: profileData.averageOrderValue,
      campaign_frequency: profileData.campaignFrequency,
      primary_business_models: profileData.primaryBusinessModels || [],
      max_placements_per_month: profileData.maxPlacementsPerMonth,
      onboarding_completed: true
    }])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating publisher profile:', error);
    throw error;
  }
  return data;
}

export async function updatePublisherProfile(userId, profileData) {
  const { data, error } = await supabase
    .from('publisher_profiles')
    .update({
      company_description: profileData.companyDescription,
      city: profileData.city,
      state: profileData.state,
      primary_category: profileData.primaryCategory,
      list_size: profileData.listSize,
      engaged_subscribers: profileData.engagedSubscribers,
      website_visitors: profileData.websiteVisitors,
      social_followers: profileData.socialFollowers,
      dr_score: profileData.drScore,
      placement_types: profileData.placementTypes || [],
      regions: profileData.regions || [],
      gender_mix: profileData.genderMix,
      age_ranges: profileData.ageRanges || [],
      household_incomes: profileData.householdIncomes || [],
      monthly_revenue: profileData.monthlyRevenue,
      average_order_value: profileData.averageOrderValue,
      campaign_frequency: profileData.campaignFrequency,
      primary_business_models: profileData.primaryBusinessModels || [],
      max_placements_per_month: profileData.maxPlacementsPerMonth,
      onboarding_completed: true
    })
    .eq('user_id', userId)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating publisher profile:', error);
    throw error;
  }
  return data;
}

export async function getPublisherProfile(userId) {
  const { data, error } = await supabase
    .from('publisher_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();
  
  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching publisher profile:', error);
    throw error;
  }
  return data;
}

export async function getAllPublishers() {
  const { data, error } = await supabase
    .from('users')
    .select(`
      *,
      publisher_profiles (*)
    `)
    .not('publisher_profiles', 'is', null);
  
  if (error) {
    console.error('Error fetching publishers:', error);
    return [];
  }
  
  // Filter to only include users with completed profiles
  return (data || []).filter(u => 
    u.publisher_profiles && 
    u.publisher_profiles.length > 0 && 
    u.publisher_profiles[0].onboarding_completed
  );
}

// ----- ADVERTISER PROFILES -----

export async function createAdvertiserProfile(userId, profileData, userData = {}) {
  const { data, error } = await supabase
    .from('advertiser_profiles')
    .insert([{
      user_id: userId,
      company_name: userData.companyLegalName || profileData.companyName || '',
      brand_name: userData.brandNameDBA || profileData.brandName || '',
      email: userData.email || profileData.email || '',
      company_description: profileData.companyDescription,
      city: profileData.city,
      state: profileData.state,
      primary_category: profileData.primaryCategory,
      monthly_budget: profileData.monthlyBudget,
      campaign_goal: profileData.campaignGoal,
      target_categories: profileData.targetCategories || [],
      target_regions: profileData.targetRegions || [],
      target_demographics: profileData.targetDemographics || {},
      onboarding_completed: true
    }])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating advertiser profile:', error);
    throw error;
  }
  return data;
}

export async function getAllAdvertisers() {
  const { data, error } = await supabase
    .from('users')
    .select(`
      *,
      advertiser_profiles (*)
    `)
    .not('advertiser_profiles', 'is', null);
  
  if (error) {
    console.error('Error fetching advertisers:', error);
    return [];
  }
  
  return (data || []).filter(u => 
    u.advertiser_profiles && 
    u.advertiser_profiles.length > 0 && 
    u.advertiser_profiles[0].onboarding_completed
  );
}

// ----- PARTNERSHIPS -----

export async function createPartnership(partnershipData) {
  const { data, error } = await supabase
    .from('partnerships')
    .insert([{
      publisher_name: partnershipData.publisherName,
      publisher_email: partnershipData.publisherEmail,
      advertiser_name: partnershipData.advertiserName,
      advertiser_email: partnershipData.advertiserEmail,
      status: partnershipData.status || 'proposed',
      proposed_by: partnershipData.proposedBy,
      placement_type: partnershipData.placementType,
      proposed_budget: partnershipData.proposedBudget,
      notes: partnershipData.notes
    }])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating partnership:', error);
    throw error;
  }
  
  // Send notification about new partnership opportunity
  if (data) {
    sendOpportunityNotification({
      recipientEmail: partnershipData.publisherEmail,
      recipientName: partnershipData.publisherName,
      partnerName: partnershipData.advertiserName,
      type: 'new_partnership'
    });
  }
  
  return data;
}

// ============================================
// NOTIFICATION SYSTEM
// ============================================
// This function can be connected to an email/SMS service like:
// - SendGrid, Resend, Mailgun (for email)
// - Twilio (for SMS)
// - Supabase Edge Functions (for serverless notifications)

export async function sendOpportunityNotification({ recipientEmail, recipientName, partnerName, type }) {
  console.log(`📧 NOTIFICATION: New ${type} for ${recipientName} (${recipientEmail}) from ${partnerName}`);
  
  // TODO: Connect to email service
  // Example with Supabase Edge Function:
  // await supabase.functions.invoke('send-notification', {
  //   body: { recipientEmail, recipientName, partnerName, type }
  // });
  
  // For now, we'll store notifications in a table for later processing
  try {
    await supabase
      .from('notifications')
      .insert([{
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        partner_name: partnerName,
        notification_type: type,
        status: 'pending',
        created_at: new Date().toISOString()
      }]);
  } catch (e) {
    // Notifications table may not exist yet - that's okay
    console.log('Notification logged (table may not exist yet):', { recipientEmail, type });
  }
  
  return true;
}

export async function getAllPartnerships() {
  const { data, error } = await supabase
    .from('partnerships')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching partnerships:', error);
    return [];
  }
  return data || [];
}

export async function updatePartnershipStatus(partnershipId, newStatus) {
  const { data, error } = await supabase
    .from('partnerships')
    .update({ status: newStatus })
    .eq('id', partnershipId)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating partnership:', error);
    throw error;
  }
  return data;
}

export async function getPartnershipsByEmail(email) {
  const { data, error } = await supabase
    .from('partnerships')
    .select('*')
    .or(`publisher_email.eq.${email},advertiser_email.eq.${email}`)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching partnerships:', error);
    return [];
  }
  return data || [];
}

// ----- CREATIVES -----

export async function createCreative(creativeData) {
  const { data, error } = await supabase
    .from('creatives')
    .insert([{
      partnership_id: creativeData.partnershipId,
      advertiser_name: creativeData.advertiserName,
      advertiser_email: creativeData.advertiserEmail,
      publisher_name: creativeData.publisherName,
      publisher_email: creativeData.publisherEmail,
      company_name: creativeData.companyName,
      headline: creativeData.headline,
      body_text: creativeData.bodyText,
      offer_details: creativeData.offerDetails,
      cta_text: creativeData.ctaText,
      cta_url: creativeData.ctaUrl,
      image_url: creativeData.imageUrl,
      logo_url: creativeData.logoUrl,
      placement_type: creativeData.placementType,
      status: 'pending'
    }])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating creative:', error);
    throw error;
  }
  return data;
}

export async function getAllCreatives() {
  const { data, error } = await supabase
    .from('creatives')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching creatives:', error);
    return [];
  }
  return data || [];
}

export async function updateCreativeStatus(creativeId, newStatus, feedback = '') {
  const updateData = { 
    status: newStatus,
    feedback: feedback || null
  };
  
  if (newStatus === 'approved') {
    updateData.approved_at = new Date().toISOString();
  }
  
  const { data, error } = await supabase
    .from('creatives')
    .update(updateData)
    .eq('id', creativeId)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating creative:', error);
    throw error;
  }
  return data;
}

export async function getCreativesByPublisherEmail(email) {
  const { data, error } = await supabase
    .from('creatives')
    .select('*')
    .eq('publisher_email', email)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching creatives:', error);
    return [];
  }
  return data || [];
}

export async function getCreativesByAdvertiserEmail(email) {
  const { data, error } = await supabase
    .from('creatives')
    .select('*')
    .eq('advertiser_email', email)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching creatives:', error);
    return [];
  }
  return data || [];
}
